/**
 * Sandbox Quick Start — first-experience inline panel shown to users
 * who land on an empty Sandbox (their own org, no portfolios yet).
 *
 * Replaces the blank-page abandonment risk with three concrete next
 * actions. The killer button is #3 "Clone a demo" — it forks an
 * installable demo pack into the user's own org so they have working
 * data to explore from minute one. Pairs with the demo packs system
 * (item 1 of the customer rollout plan).
 *
 * This component is purely presentational. The parent (typically
 * ContextSelector) decides when to render it (sandbox mode + empty
 * portfolio hierarchy + no search query).
 *
 * Replaces the inline panel that previously lived in ContextSelector at
 * line 918 (which had only 2 buttons: Create + Switch to Demo). The
 * Clone-a-demo path is the new addition and the highest-leverage
 * onboarding move — it pairs with the demo packs system from item 1.
 *
 * Design rationale + dependency tracking lives in
 * Tasks/Customer_Rollout_Build_Plan_2026-05-28.md (item 2 / Redesign Plan #5).
 */

import { useNavigate } from 'react-router-dom'
import { Plus, Upload, Boxes, Box } from 'lucide-react'

export interface SandboxQuickStartProps {
  /** "Create your first portfolio" click handler. */
  onCreatePortfolio: () => void
  /** "Import from a file" click handler. Pass undefined to render as
   *  "Coming soon" / disabled (Card 2 is deferred per the build plan). */
  onImportFromFile?: () => void
  /** Optional escape hatch — show a small "Switch to Demo mode" link
   *  underneath if the user really wants to see a fully-loaded example
   *  instead of building their own. */
  onSwitchToDemo?: () => void
}

export function SandboxQuickStart(props: SandboxQuickStartProps) {
  const navigate = useNavigate()
  const { onCreatePortfolio, onImportFromFile, onSwitchToDemo } = props

  function handleCloneDemo() {
    navigate('/config/demo-library')
  }

  return (
    <div className="bg-white rounded-xl border border-border shadow-card p-8 max-w-3xl mx-auto">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="w-16 h-16 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-4">
          <Box size={28} />
        </div>
        <h3 className="text-heading-lg font-display mb-2">Welcome to your sandbox</h3>
        <p className="text-body-md text-text-secondary max-w-md mx-auto leading-relaxed">
          Your org is empty. Pick one of these three paths to get going — you can change your mind later.
        </p>
      </div>

      {/* Three options */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
        <QuickStartCard
          icon={<Plus size={26} />}
          label="Create your first portfolio"
          description="Start blank. Add projects, set budgets, grow from there."
          onClick={onCreatePortfolio}
          tone="primary"
        />
        <QuickStartCard
          icon={<Upload size={26} />}
          label="Import from a file"
          description="CSV or schedule file. We'll map columns to portfolio + project shape."
          onClick={onImportFromFile}
          disabled={!onImportFromFile}
          tone="default"
          badge={!onImportFromFile ? 'Coming soon' : undefined}
        />
        <QuickStartCard
          icon={<Boxes size={26} />}
          label="Clone a demo to start from"
          description="Fork a working demo (Gentex aerospace, Boeing MDC2) into your org. Uninstall anytime."
          onClick={handleCloneDemo}
          tone="accent"
          highlight
        />
      </div>

      {/* Escape hatch */}
      {onSwitchToDemo && (
        <div className="pt-4 border-t border-border text-center">
          <p className="text-[12px] text-text-tertiary">
            Just exploring?{' '}
            <button
              type="button"
              onClick={onSwitchToDemo}
              className="text-brand-600 hover:text-brand-700 hover:underline font-medium"
            >
              Switch to Demo mode
            </button>
            {' '}to see a fully-loaded example.
          </p>
        </div>
      )}
    </div>
  )
}

interface QuickStartCardProps {
  icon: React.ReactNode
  label: string
  description: string
  onClick?: () => void
  disabled?: boolean
  tone: 'default' | 'primary' | 'accent'
  highlight?: boolean
  badge?: string
}

function QuickStartCard(props: QuickStartCardProps) {
  const { icon, label, description, onClick, disabled, tone, highlight, badge } = props
  const toneClass =
    tone === 'primary'
      ? 'border-mach12-primary/40 bg-mach12-primary/5 hover:border-mach12-primary hover:bg-mach12-primary/10'
      : tone === 'accent'
        ? 'border-emerald-300 bg-emerald-50 hover:border-emerald-500 hover:bg-emerald-100'
        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
  const iconToneClass =
    tone === 'primary' ? 'text-mach12-primary' : tone === 'accent' ? 'text-emerald-700' : 'text-slate-500'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`relative text-left rounded-lg border-2 p-4 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${toneClass} ${
        highlight ? 'ring-2 ring-emerald-200 ring-offset-2' : ''
      }`}
    >
      {badge && (
        <span className="absolute top-2 right-2 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 border border-slate-200">
          {badge}
        </span>
      )}
      <div className={`mb-2.5 ${iconToneClass}`}>{icon}</div>
      <h4 className="text-sm font-semibold text-slate-900 mb-1">{label}</h4>
      <p className="text-xs text-text-secondary leading-relaxed">{description}</p>
    </button>
  )
}
